import { abonnementDeCetAppareil } from '../partage/abonnementDeCetAppareil';
import { api, EchecApi } from '../partage/api/client';
import { fuseauDuNavigateur } from '../partage/datesEnMots';
import { cleEnOctets, envoiDeLAbonnement, faitAvecUneAutreCle, notificationsPossibles } from './notificationsDuTelephone';

/**
 * Cet appareil et ses notifications (chantier notifications-socle, ticket 03) : ce qui parle au
 * navigateur (la permission, l'abonnement du service worker) et au serveur, par
 * `partage/api/client.ts`. Les règles : `notificationsDuTelephone.ts`.
 *
 * L'abonnement vit dans le navigateur ; le serveur en garde l'adresse d'envoi et les clés. Le
 * navigateur fait foi pour ce que dit l'interrupteur (« l'état réel de cet appareil »).
 */

/**
 * Le fuseau du téléphone ; s'il ne le dit pas, celui de la France, où l'app sert (choix du
 * ticket : un fuseau manquant laisserait l'appareil sans abonnement).
 */
function fuseau(): string {
  return fuseauDuNavigateur() ?? 'Europe/Paris';
}

/** Le service worker de l'app, actif ; `null` s'il n'est pas enregistré (en développement, ou refusé). */
async function serviceWorkerActif(): Promise<ServiceWorkerRegistration | null> {
  if (!notificationsPossibles(window)) return null;
  const inscrit = await navigator.serviceWorker.getRegistration();
  if (!inscrit) return null;
  return navigator.serviceWorker.ready;
}

/** La permission du navigateur, telle quelle ; `default` quand il ne sait pas notifier. */
export function permissionDeCetAppareil(): NotificationPermission {
  return notificationsPossibles(window) ? Notification.permission : 'default';
}

/** Ce qu'a donné l'interrupteur allumé. `pas-fait` : la fenêtre de permission fermée sans réponse. */
export type IssueDeLAbonnement = 'abonne' | 'refusee' | 'impossible' | 'hors-ligne' | 'pas-fait' | 'echec';

/**
 * Allumer : demander la permission (la fenêtre du téléphone), abonner le navigateur avec la clé
 * publique du serveur, puis donner l'abonnement au serveur. Si le serveur ne le prend pas, le
 * navigateur est désabonné : l'interrupteur ne dit jamais « oui » pour un appareil que le compte
 * ne connaît pas.
 */
export async function abonnerCetAppareil(): Promise<IssueDeLAbonnement> {
  if (!notificationsPossibles(window)) return 'impossible';
  const permission = await Notification.requestPermission();
  if (permission === 'denied') return 'refusee';
  if (permission !== 'granted') return 'pas-fait';

  try {
    const actif = await serviceWorkerActif();
    if (!actif) return 'impossible';
    const { clePublique } = await api.cleDesNotifications();
    // Un abonnement d'avant (une autre clé du serveur) empêcherait le nouveau : il part d'abord.
    const ancien = await actif.pushManager.getSubscription();
    if (ancien) await ancien.unsubscribe().catch(() => false);
    await abonnerLeNavigateur(actif, clePublique);
    return 'abonne';
  } catch (erreur) {
    return erreur instanceof EchecApi && erreur.code === 'hors_ligne' ? 'hors-ligne' : 'echec';
  }
}

/**
 * Abonner le navigateur avec la clé publique du serveur, puis donner l'abonnement au serveur. Si
 * le serveur ne le prend pas, le navigateur est désabonné, et l'erreur remonte.
 */
async function abonnerLeNavigateur(actif: ServiceWorkerRegistration, clePublique: string): Promise<void> {
  let abonnement: PushSubscription | null = null;
  try {
    abonnement = await actif.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: cleEnOctets(clePublique) });
    const envoi = envoiDeLAbonnement(abonnement.toJSON(), fuseau());
    if (!envoi) throw new Error('abonnement incomplet');
    await api.abonnerCetAppareil(envoi);
  } catch (erreur) {
    await abonnement?.unsubscribe().catch(() => false);
    throw erreur;
  }
}

/**
 * Les clés du serveur ont changé (spec du chantier notifications-sortes, « Ce que le socle a
 * laissé ») : l'abonnement du navigateur, fait avec l'ancienne clé publique, ne reçoit plus rien.
 * Le serveur oublie l'ancien, le navigateur s'en défait et en fait un neuf, donné au serveur, sans
 * rien dire ni redemander la permission (elle est donnée ; sans elle, rien). `true` : réabonné
 * (ou essayé : l'ancien est parti), « vu » n'a plus à partir. Lève si la clé du serveur ne se
 * lit pas, avant d'avoir rien touché.
 */
async function reabonnerSiLaCleAChange(abonnement: PushSubscription): Promise<boolean> {
  const { clePublique } = await api.cleDesNotifications();
  if (!faitAvecUneAutreCle(abonnement.options?.applicationServerKey, clePublique)) return false;
  if (permissionDeCetAppareil() !== 'granted') return false;
  const actif = await serviceWorkerActif();
  if (!actif) return false;
  await api.desabonnerCetAppareil(abonnement.endpoint).catch(() => undefined);
  await abonnement.unsubscribe().catch(() => false);
  // Raté (pas de réseau, serveur qui refuse) : l'appareil reste désabonné, l'interrupteur le dira.
  await abonnerLeNavigateur(actif, clePublique).catch(() => undefined);
  return true;
}

/** Une fois par chargement de l'app : rouvrir un écran du téléphone ne repart pas au serveur. */
let dejaVu = false;

/**
 * À l'ouverture de l'app, sur un appareil abonné : s'il l'a été avec une autre clé du serveur, il
 * se réabonne (`reabonnerSiLaCleAChange`) ; sinon « cet appareil est vu » (son fuseau, sa
 * dernière vue). Sans bloquer ni rien dire si ça échoue. Si le compte ne connaît plus cette
 * adresse (`abonnement_inconnu`), l'abonnement du navigateur lui est redonné : l'interrupteur dit
 * « oui », le compte doit le savoir.
 */
export async function noterCetAppareilVu(): Promise<void> {
  if (dejaVu) return;
  dejaVu = true;
  try {
    const abonnement = await abonnementDeCetAppareil();
    if (!abonnement) return;
    // La clé qui ne se lit pas (pas de réseau, serveur muet) ne retient pas « vu ».
    if (await reabonnerSiLaCleAChange(abonnement).catch(() => false)) return;
    try {
      await api.appareilVu(abonnement.endpoint, fuseau());
    } catch (erreur) {
      if (!(erreur instanceof EchecApi) || erreur.code !== 'abonnement_inconnu') return;
      const envoi = envoiDeLAbonnement(abonnement.toJSON(), fuseau());
      if (envoi) await api.abonnerCetAppareil(envoi);
    }
  } catch {
    // Rien à dire : l'app s'ouvre comme d'habitude.
  }
}
